/**
 * NotificationBell Component
 * Header bell icon with unread badge and notification dropdown
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Bell, CheckCheck } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { socketClient } from '@/lib/socket'
import { cn } from '@/lib/utils'
import { NotificationRow } from '@/components/notifications/NotificationRow'
import { CrmNotification, getNotificationLink } from '@/lib/notifications'

export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<CrmNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const hasLoadedRef = useRef(false)

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount()
  }, [])

  // Listen for real-time notifications
  useEffect(() => {
    const handleNotification = (notification: any) => {
      setUnreadCount((prev) => prev + 1)
      // Prepend to list if popover is open
      setNotifications((prev) => [notification, ...prev])
    }

    socketClient.onNotification(handleNotification)
    return () => {
      socketClient.offNotification(handleNotification)
    }
  }, [])

  const fetchUnreadCount = async () => {
    try {
      const { count } = await apiClient.getUnreadNotificationCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }

  const fetchNotifications = useCallback(async () => {
    if (hasLoadedRef.current) return
    try {
      setLoading(true)
      const { data } = await apiClient.getNotifications(1, 30)
      setNotifications(data)
      hasLoadedRef.current = true
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen) {
      fetchNotifications()
    } else {
      // Reset so next open refetches
      hasLoadedRef.current = false
    }
  }

  const handleNotificationClick = async (notification: CrmNotification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await apiClient.markNotificationRead(notification.id)
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        )
        setUnreadCount((prev) => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Failed to mark notification read:', error)
      }
    }

    // Navigate to entity
    const link = getNotificationLink(notification)
    if (link) {
      navigate(link)
      setOpen(false)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await apiClient.markAllNotificationsRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          {/* Subtle pulse draws the eye when there are unread notifications.
              It's a slow ~2s ring (WCAG 2.3.1 safe) and is disabled under
              prefers-reduced-motion (see index.css). */}
          <Bell className={cn('h-5 w-5', unreadCount > 0 && 'animate-bell-pulse')} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground"
              aria-hidden="true"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-foreground"
              onClick={handleMarkAllRead}
              aria-label="Mark all notifications as read"
            >
              <CheckCheck className="mr-1 h-3 w-3" aria-hidden="true" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-foreground">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-foreground">No notifications</div>
          ) : (
            // Pinned to the popover's width (w-80): Radix ScrollArea lets content grow wider
            // than the viewport, which pushed the "New" label off the edge and stopped long
            // titles truncating.
            <div className="w-80 divide-y">
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  truncate
                />
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs text-foreground"
            onClick={() => {
              setOpen(false)
              navigate('/notifications')
            }}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
